/** @jsxImportSource @emotion/react */
import { cloneDeep } from "lodash";
import React from "react";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { editCampaign } from "../api/Campaign";
import { TempCampaignAtom } from "../atoms/CampaignAtom";
import { CampaignUnsavedChangesAtom } from "../atoms/CampaignUnsavedChangesAtom";
import { UserAtom } from "../atoms/UserAtom";
import Warning from "../components/Modal/container/Warning";
import { useModal } from "../components/Modal/Modal";
import { getBody } from "../components/NewCampaign/Create/TopBar";
import { useValidateCampaign } from "../pages/create-campaign";
import { Campaign, INodeTypes } from "../types/CampaignTypes";
import { toast } from "react-toastify";

const isLoadingAtom = atom<boolean>({
  key: "UnsavedWarningLoadingAtom",
  default: false,
});

export function useUnsavedChangesBlocker() {
  const modal = useModal();
  const { validateCampaign } = useValidateCampaign();

  const user = useRecoilValue(UserAtom);

  const tempCampaign = useRecoilValue<Campaign | null>(TempCampaignAtom);

  const [shouldBlock, setShouldBlock] = useRecoilState(
    CampaignUnsavedChangesAtom
  );

  const setIsLoading = useSetRecoilState(isLoadingAtom);

  async function handleEditCampaign() {
    setIsLoading(true);
    try {
      if (!tempCampaign) {
        toast("Could not save campaign", { type: "error" });
        modal.popModal();
        setIsLoading(false);
        return false;
      }
      const erroredNodes = await validateCampaign();

      if (erroredNodes.length) {
        toast("There are errors in your campaign", { type: "error" });
        setIsLoading(false);
        modal.popModal();
        return false;
      }

      const fontFamily = user?.emailOptions.font.family;
      const fontSize = user?.emailOptions.font.size;
      const maxWidth = user?.emailOptions.maxWidth;

      let copyTempCampaign = cloneDeep(tempCampaign);

      copyTempCampaign.nodes = copyTempCampaign.nodes.map((node) => {
        if (node.type === INodeTypes.Email && node.email?.interestingURLs) {
          let interestingURLs = node.email.interestingURLs;
          let sanitizedInterestTags: { [key: string]: string[] } = {};
          if (interestingURLs) {
            Object.keys(interestingURLs).forEach((tag) => {
              sanitizedInterestTags[tag.split(".").join("|")] =
                interestingURLs[tag];
            });
          }
          node.email.interestingURLs = sanitizedInterestTags;
          node.email.body = getBody(
            node.email.body,
            fontFamily,
            fontSize,
            maxWidth,
            false
          );
          return node;
        } else {
          return node;
        }
      });

      await editCampaign(copyTempCampaign._id, copyTempCampaign as any);

      return true;
    } catch (err) {
      console.log(err);
      toast(err.message, { type: "error" });
      setIsLoading(false);
    } finally {
      modal.popModal();
      setIsLoading(false);
    }
  }

  const campaignUnsavedChangesBlocker = (onProcceed: () => any) => {
    if (shouldBlock) {
      modal.pushModal(
        <Warning
          title="Unsaved Changes"
          description="Do you want to save the changes made to this campaign? Your changes will be lost if you don’t save them."
          buttonsName={["Discard", "Save"]}
          icon="/unsavedChanges.svg"
          onCancelClicked={() => {
            setShouldBlock(false);
            modal.popModal();
            onProcceed();
          }}
          onOkClicked={async () => {
            const shouldProcceed = await handleEditCampaign();
            if (!shouldProcceed) return;
            setShouldBlock(false);
            onProcceed();
          }}
          hasThirdButton={true}
          labelThirdButton="Cancel"
          onClickThirdButton={() => {
            modal.popModal();
          }}
          IsLoadingAtom={isLoadingAtom}
        />
      );
    } else {
      onProcceed();
    }
  };

  return { campaignUnsavedChangesBlocker };
}
